/**
 * Thin scrolling safety/early-access ticker.
 *
 * Replaces the old wall-of-red `DemoBanner` block with a one-line strip
 * (28px) that scrolls horizontally so the message stays visible without
 * stealing two inches of real estate.
 *
 * Mirrors the original safety semantics:
 *  - links to /demo-safety for the full notice
 *  - hides itself on /pair-device, /consent/parental/verify (focus modes)
 *  - dismissible via the close button (state in sessionStorage so it
 *    re-appears on the next visit but not on every page nav)
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";

const STORAGE_KEY = "sonspeak_ticker_dismissed_v1";
const FOCUS_ROUTES = new Set([
  "/pair-device",
  "/consent/parental/verify",
  "/billing/success",
  // The interactive demo board is a focus mode: it locks to the viewport
  // like the real child surface. The safety notice renders inside the
  // page's "For parents" section instead.
  "/demo",
]);

const MESSAGES = [
  "SonSpeak is in early access — it is not a medical, clinical, or emergency tool. For real-world safety or emergencies, contact your doctor or 911.",
  "Need help? Email support@sonspeak.com — we read every message.",
];

export default function TickerBanner() {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(STORAGE_KEY) === "1");
    } catch {
      /* private mode */
    }
  }, []);

  if (dismissed) return null;
  if (FOCUS_ROUTES.has(location.pathname)) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* noop */
    }
    setDismissed(true);
  };

  // Build a single track that repeats messages enough times to fill any
  // viewport. CSS marquee animation handles the scroll. Each message ends
  // with the safety-notice link so users can always click through.
  const repeated = Array.from({ length: 3 }).flatMap(() => MESSAGES);

  return (
    <div
      className="relative w-full bg-[color:var(--coral)] text-white border-b-[3px] border-[color:var(--ink)] overflow-hidden"
      data-testid="ticker-banner"
      role="region"
      aria-label="Important safety notice"
    >
      {/* Mobile: static, wrapped, always fully readable. No marquee — a
          scrolling one-liner reads as "clipped" at any given frame. */}
      <div className="sm:hidden flex items-start gap-2 px-3 py-1.5 pr-9 text-xs font-bold leading-snug">
        <AlertTriangle size={12} className="shrink-0 mt-0.5" aria-hidden />
        <p className="min-w-0">
          <span className="uppercase tracking-wider mr-1">Early access —</span>
          not a medical, clinical, or emergency tool. For emergencies, call your doctor or 911.{" "}
          <Link to="/demo-safety" className="underline">
            Learn more →
          </Link>
        </p>
      </div>

      {/* Desktop / tablet: horizontal marquee ticker, unchanged. */}
      <div className="hidden sm:block" style={{ height: "32px" }}>
        <div className="ticker-track flex items-center gap-12 whitespace-nowrap h-full text-xs sm:text-sm font-bold px-4">
          {repeated.map((msg, i) => (
            <span key={i} className="inline-flex items-center gap-2 shrink-0">
              <AlertTriangle size={14} aria-hidden />
              <span>{msg}</span>
              <Link
                to="/demo-safety"
                className="underline ml-1 hover:text-[color:var(--gold)]"
              >
                Learn more →
              </Link>
            </span>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={dismiss}
        className="absolute top-1/2 -translate-y-1/2 right-2 sm:right-3 text-white hover:text-[color:var(--gold)] z-10"
        aria-label="Dismiss notice"
        data-testid="ticker-banner-dismiss"
      >
        <X size={16} />
      </button>
      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-track {
          animation: ticker-scroll 60s linear infinite;
          width: max-content;
        }
        .ticker-track:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .ticker-track { animation: none; }
        }
      `}</style>
    </div>
  );
}
